import { routes } from '@/config/routes'
import type { ChartConfig, ChartSerie } from '@/utils/analytics/transformDataToBestPerformingVaultChartConfig'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getValueColor } from '@/utils/styles/getValueColor'
import { formatAmount, formatDate } from '@vaultsfyi/common/utils'
import { Link, generatePath } from 'react-router'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ChartBrush, SingleAreaBrushPanorama } from '../charts/chartBrush'
import { ChartContainer } from '../charts/chartContainer'
import { ChartTooltip } from '../charts/chartTooltip'
import { ChartWatermark } from '../charts/chartWatermark'
import { Button } from '../common/button'
import { ProtocolImage } from '../common/protocolImage'

interface BestPerformingVaultChartProps {
  config: ChartConfig
  hideLegend?: boolean
  showBrush?: boolean
}

export const BestPerformingVaultChart = ({ config, hideLegend, showBrush = false }: BestPerformingVaultChartProps) => {
  const { data, series } = config
  const brushDataKey = series[0]?.key

  return (
    <div className="flex flex-col gap-2 lg:flex-row lg:gap-4">
      <div className="flex w-full">
        <div className="flex justify-center max-lg:items-center lg:flex-col">
          <p className="text-center text-sm [writing-mode:sideways-lr]">Share price chg.</p>
        </div>
        <div className="min-w-0 flex-1">
          <ChartContainer className={cn('w-full', showBrush ? 'h-80 min-h-80' : 'h-72 min-h-72')}>
            <ResponsiveContainer>
              <AreaChart data={data}>
                <XAxis
                  dataKey="timestamp"
                  tickLine={false}
                  tickMargin={12}
                  type="number"
                  interval="preserveStartEnd"
                  domain={['dataMin', 'dataMax']}
                  tickCount={7}
                  tickFormatter={(timestamp) => formatDate({ timestamp })}
                />
                <YAxis
                  width={52}
                  tickMargin={0}
                  tickLine={false}
                  tickFormatter={(value) => `${formatAmount(value, { notation: 'compact' })}%`}
                  interval="preserveStartEnd"
                  padding={{ top: 0, bottom: 0 }}
                  domain={([dataMin, dataMax]) => [dataMin, dataMax]}
                />
                <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" />
                <Tooltip content={({ payload }) => <PerformanceChartTooltip data={payload} series={series} />} />

                {series.map((serie) => (
                  <Area
                    key={serie.key}
                    dataKey={serie.key}
                    type="monotone"
                    stroke={serie.color}
                    strokeWidth={2}
                    fill="transparent"
                    isAnimationActive={false}
                  />
                ))}
                <ChartWatermark />
                {showBrush && brushDataKey && (
                  <ChartBrush data={data}>
                    <SingleAreaBrushPanorama
                      color="rgb(var(--chart-violet))"
                      dataKey={brushDataKey}
                      gradientId="bestPerformingBrushFill"
                    />
                  </ChartBrush>
                )}
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
      </div>
      {!hideLegend && series.length > 0 && (
        <div className="mt-2 flex w-full flex-row flex-wrap gap-2 lg:mt-0 lg:max-w-56 lg:flex-col lg:flex-nowrap lg:justify-start lg:gap-3">
          {series.map((serie) => (
            <LegendEntry key={serie.key} serie={serie} />
          ))}
        </div>
      )}
    </div>
  )
}

const LegendEntry = ({ serie }: { serie: ChartSerie }) => {
  return (
    <div className="flex max-w-full items-center gap-2">
      <div className="size-3 min-w-3 shrink-0 rounded-full" style={{ backgroundColor: serie.color }} />
      <ProtocolImage protocol={serie.protocol ?? { name: '' }} className="size-5 shrink-0" />
      <Button
        variant="default"
        size="default"
        className="min-w-0 flex-1 items-baseline justify-start gap-1 text-xs lg:text-sm"
        asChild
      >
        <Link
          to={generatePath(routes.opportunity, { vaultId: serie.vaultId, network: serie.network })}
          prefetch="intent"
        >
          <p className="min-w-0 truncate">{serie.name}</p>
          <span className="shrink-0 first-letter:uppercase">({getNetworkName(serie.network)})</span>
        </Link>
      </Button>
    </div>
  )
}

interface PerformanceChartTooltipProps {
  data?: any[]
  series: ChartSerie[]
}

const PerformanceChartTooltip = ({ data, series }: PerformanceChartTooltipProps) => {
  if (!data || data.length === 0) return null

  const timestamp = data[0]?.payload?.timestamp
  if (!timestamp) return null

  return (
    <ChartTooltip>
      <span className="mb-2 block">{formatDate({ timestamp, showYear: true })}</span>
      {[...data]
        .filter((entry) => entry.value !== null && entry.value !== undefined)
        .sort((a, b) => b.value - a.value)
        .map((entry) => {
          const serie = series.find((s) => s.key === entry.dataKey)
          if (!serie) return null

          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="size-3 min-w-3 rounded-full" style={{ backgroundColor: serie.color }} />
                <span className="text-sm">{serie.name}</span>
              </div>
              <span className={cn('font-bold tabular-nums', getValueColor(entry.value))}>
                {entry.value > 0 ? '+' : ''}
                {formatAmount(entry.value, { notation: 'compact', minimumFractionDigits: 2 })}%
              </span>
            </div>
          )
        })}
    </ChartTooltip>
  )
}
